import { useTranslation } from 'react-i18next';
import { Slider } from '@radix-ui/themes';
import { Card, Flex, Text } from '@/components/common';
import type { ProductPrice } from '@/types';

interface ProductSliderProps {
  products: ProductPrice[];
  selectedIndex: number;
  onSelect: (index: number) => void;
}

function getMonthlyPrice(product: ProductPrice) {
  return product.prices.month ?? Object.values(product.prices)[0];
}

export function ProductSlider({ products, selectedIndex, onSelect }: ProductSliderProps) {
  const { t } = useTranslation('subscription');
  const product = products[selectedIndex];
  const selectedPrice = getMonthlyPrice(product);
  const minPrice = getMonthlyPrice(products[0]);
  const maxPrice = getMonthlyPrice(products[products.length - 1]);

  return (
    <Flex direction="column" m="5" gap="4">
      <Card variant="surface">
        <Flex align="center" direction="column" gap="3">
          <Flex align="baseline" gap="1">
            <Text as="span" size="8" weight="medium">
              {selectedPrice.formattedPrice}
            </Text>
            <Text as="span" size="3">
              / {selectedPrice.interval}
            </Text>
          </Flex>
          <Text align="center" size="1" color="gray">
            {t('paywall.trialNote')}
          </Text>
        </Flex>
      </Card>

      <Flex direction="column" gap="2">
        <Flex justify="between">
          <Text size="1" color="gray">
            {t('paywall.minLabel', { price: minPrice.formattedPrice })}
          </Text>
          <Text size="1" color="gray">
            {maxPrice.formattedPrice}
          </Text>
        </Flex>

        <Slider
          max={products.length - 1}
          min={0}
          onValueChange={([value]) => onSelect(value)}
          step={1}
          value={[selectedIndex]}
        />

        <Text align="center" size="1" color="gray">
          {t('paywall.sliderHint')}
        </Text>
      </Flex>
    </Flex>
  );
}
